import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Menu, X, ChevronRight, Globe, LogIn, UserPlus, User } from 'lucide-react';
import { ZaloIcon } from './zalo-icon';
import i18next from 'i18next';
import routesMap from 'layouts/routesMap';
import { getStorageData, STORAGE_KEY } from 'utils/storage';
import './Header.scss';

const LOGO_URL = '/assets/img/Logo.jpg';

const PUBLIC_HOME_URL = `${window.location.origin}${routesMap.TRANG_CHU}`;
const SSO_URL = `${process.env.REACT_APP_URL_SSO}/login?returnUrl=${encodeURIComponent(PUBLIC_HOME_URL)}&version=1.0.0&source=2`;
const REGISTER_URL = `${SSO_URL}&isRegister=true`;

// ─── Types ───────────────────────────────────────────────────────────────────
type Locale = 'vi' | 'en';

interface NavChild {
  name: string;
  href: string;
}

interface NavLink {
  name: string;
  href: string;
  children?: NavChild[];
}

// ─── Translations ─────────────────────────────────────────────────────────────
const i18n: Record<Locale, { nav: NavLink[]; getQuote: string; login: string; register: string; connectWithUs: string; contactInfo: string }> = {
  vi: {
    nav: [
      { name: 'Trang chủ', href: '/trang-chu' },
      { name: 'Giới thiệu', href: '/gioi-thieu' },
      { name: 'Tra cứu', href: '/tra-cuu' },
      {
        name: 'Tin tức', href: '/tin-tuc',
        children: [
          { name: 'Tin tức GHTM', href: '/tin-tuc/ghtm' },
          { name: 'Tin tức ngành', href: '/tin-tuc/nganh' },
        ],
      },
      { name: 'Liên hệ', href: '/lien-he' },
    ],
    getQuote: 'Báo giá ngay',
    login: 'Đăng nhập',
    register: 'Đăng ký',
    connectWithUs: 'Kết nối với chúng tôi:',
    contactInfo: 'Thông tin liên hệ',
  },
  en: {
    nav: [
      { name: 'Home', href: '/trang-chu' },
      { name: 'About Us', href: '/gioi-thieu' },
      { name: 'Track', href: '/tra-cuu' },
      {
        name: 'News', href: '/tin-tuc',
        children: [
          { name: 'GHTM News', href: '/tin-tuc/ghtm' },
          { name: 'Industry News', href: '/tin-tuc/nganh' },
        ],
      },
      { name: 'Contact', href: '/lien-he' },
    ],
    getQuote: 'Get a Quote',
    login: 'Login',
    register: 'Register',
    connectWithUs: 'Connect with us:',
    contactInfo: 'Contact Info',
  },
};


// ─── Helper ───────────────────────────────────────────────────────────────────
function cx(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Header Component ─────────────────────────────────────────────────────────
const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const savedLocale = (localStorage.getItem('lang') as Locale) || 'vi';
  const [locale, setLocale] = useState<Locale>(savedLocale);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const t = i18n[locale];
  const isLogged = Boolean(getStorageData(STORAGE_KEY.ACCESS_TOKEN));
  const customerPageLabel = locale === 'vi' ? 'Trang khách hàng' : 'Customer';

  // ── Sync language with i18next
  useEffect(() => {
    i18next.changeLanguage(locale);
    localStorage.setItem('lang', locale);
  }, [locale]);

  // ── Scroll detection
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // ── Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const isActive = (link: NavLink) =>
    location.pathname === link.href ||
    (link.children?.some(c => location.pathname === c.href) ?? false);

  // Dropdown hover handlers with delay to prevent flicker
  const handleDropdownEnter = (name: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <>
      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div className="hdr-topbar">
        <div className="hdr-container hdr-topbar__inner">
          {/* Contact info */}
          <div className="hdr-topbar__contacts">
            <a href="tel:0979260080" className="hdr-topbar__link">
              <Phone size={14} />
              <span style={{ fontWeight: 600 }}>0374 888 419</span>
            </a>
            <a href="mailto:contact@mklexpress.com" className="hdr-topbar__link hdr-topbar__link--sm">
              <Mail size={14} />
              <span>contact@mklexpress.com</span>
            </a>
            <span className="hdr-topbar__link hdr-topbar__link--lg">
              <MapPin size={14} />
              <span>60 Trương Công Giai, Cầu Giấy, Hà Nội</span>
            </span>
          </div>

          {/* Social + Language */}
          <div className="hdr-topbar__right">
            <span className="hdr-topbar__connect">{t.connectWithUs}</span>
            <div className="hdr-topbar__socials">
              <a
                href="https://www.facebook.com/giaohangthongminh"
                target="_blank" rel="noopener noreferrer"
                className="hdr-topbar__social-link"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://zalo.me/3257775966701342779"
                target="_blank" rel="noopener noreferrer"
                className="hdr-topbar__social-link"
                aria-label="Zalo"
              >
                <ZaloIcon className="hdr-icon" />
              </a>
            </div>
            {/* Language switcher */}
            <div className="hdr-lang">
              <Globe size={14} />
              <button
                onClick={() => setLocale('vi')}
                className={cx('hdr-lang__btn', locale === 'vi' && 'hdr-lang__btn--active')}
              >
                VI
              </button>
              <span className="hdr-lang__sep">|</span>
              <button
                onClick={() => setLocale('en')}
                className={cx('hdr-lang__btn', locale === 'en' && 'hdr-lang__btn--active')}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Navigation ─────────────────────────────────────────────── */}
      <header className={cx('hdr-main', isScrolled && 'hdr-main--scrolled')}>
        <div className="hdr-container hdr-main__inner">
          {/* Logo */}
          <Link to="/trang-chu" className="hdr-logo" aria-label="GHTM - Giao Hàng Thông Minh">
            <div className="hdr-logo__img-wrap">
              <img src={LOGO_URL} alt="GHTM Logo" className="hdr-logo__img" />
            </div>
            <div className="hdr-logo__text">
              <span className="hdr-logo__name">GHTM</span>
              <span className="hdr-logo__tagline">Giao Hàng Thông Minh</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hdr-nav" aria-label="Navigation chính">
            {t.nav.map(link => (
              link.children ? (
                <div
                  key={link.name}
                  className="hdr-nav__item hdr-nav__item--dropdown"
                  onMouseEnter={() => handleDropdownEnter(link.name)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <Link
                    to={link.href}
                    className={cx('hdr-nav__link', isActive(link) && 'hdr-nav__link--active')}
                  >
                    {link.name}
                    <span className="hdr-nav__underline" />
                  </Link>
                  <div className={cx('hdr-dropdown', openDropdown === link.name && 'hdr-dropdown--open')}>
                    <div className="hdr-dropdown__panel">
                      <div className="hdr-dropdown__accent" />
                      {link.children.map((child, idx) =>
                        child.href.startsWith('http') ? (
                          <a
                            key={idx}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hdr-dropdown__item"
                          >
                            {child.name}
                          </a>
                        ) : (
                          <Link
                            key={idx}
                            to={child.href}
                            className={cx(
                              'hdr-dropdown__item',
                              location.pathname === child.href && 'hdr-dropdown__item--active'
                            )}
                          >
                            {child.name}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div key={link.name} className="hdr-nav__item">
                  <Link
                    to={link.href}
                    className={cx('hdr-nav__link', isActive(link) && 'hdr-nav__link--active')}
                  >
                    {link.name}
                    <span className="hdr-nav__underline" />
                  </Link>
                </div>
              )
            ))}
          </nav>

          {/* Auth buttons + mobile toggle */}
          <div className="hdr-actions">
            {isLogged ? (
              <Link to={routesMap.THONG_KE_TIEN_HANG} className="hdr-auth__register">
                <User size={16} />
                <span>{customerPageLabel}</span>
              </Link>
            ) : (
              <>
                <a href={SSO_URL} className="hdr-auth__login">
                  <LogIn size={16} />
                  <span>{t.login}</span>
                </a>
                <a href={REGISTER_URL} className="hdr-auth__register">
                  <UserPlus size={16} />
                  <span>{t.register}</span>
                </a>
              </>
            )}

            <button
              className={cx('hdr-burger', isMobileMenuOpen && 'hdr-burger--open')}
              onClick={() => setIsMobileMenuOpen(v => !v)}
              aria-label={isMobileMenuOpen ? 'Đóng menu' : 'Mở menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile Menu ─────────────────────────────────────────────────── */}
      {/* Backdrop */}
      <div
        className={cx('hdr-backdrop', isMobileMenuOpen && 'hdr-backdrop--visible')}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cx('hdr-drawer', isMobileMenuOpen && 'hdr-drawer--open')}
        role="dialog"
        aria-modal="true"
        aria-label="Menu điều hướng"
      >
        <div className="hdr-drawer__body">
          {/* Language switcher mobile */}
          <div className="hdr-drawer__lang">
            <Globe size={15} className="hdr-drawer__lang-icon" />
            <button
              onClick={() => setLocale('vi')}
              className={cx('hdr-drawer__lang-btn', locale === 'vi' && 'hdr-drawer__lang-btn--active')}
            >
              Tiếng Việt
            </button>
            <button
              onClick={() => setLocale('en')}
              className={cx('hdr-drawer__lang-btn', locale === 'en' && 'hdr-drawer__lang-btn--active')}
            >
              English
            </button>
          </div>

          {/* Nav links */}
          <nav className="hdr-drawer__nav" aria-label="Navigation mobile">
            {t.nav.map(link => (
              <div key={link.name} className="hdr-drawer__nav-group">
                <Link
                  to={link.href}
                  className={cx('hdr-drawer__nav-link', isActive(link) && 'hdr-drawer__nav-link--active')}
                >
                  {link.name}
                  {link.children && (
                    <ChevronRight
                      size={16}
                      className="hdr-drawer__chevron hdr-drawer__chevron--has-children"
                    />
                  )}
                </Link>
                {link.children && (
                  <div className="hdr-drawer__sub">
                    {link.children.map((child, idx) =>
                      child.href.startsWith('http') ? (
                        <a
                          key={idx}
                          href={child.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hdr-drawer__sub-link"
                        >
                          {child.name}
                        </a>
                      ) : (
                        <Link
                          key={idx}
                          to={child.href}
                          className={cx(
                            'hdr-drawer__sub-link',
                            location.pathname === child.href && 'hdr-drawer__sub-link--active'
                          )}
                        >
                          {child.name}
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Contact info */}
          <div className="hdr-drawer__contact">
            <h4 className="hdr-drawer__contact-title">{t.contactInfo}</h4>
            <a href="tel:0979260080" className="hdr-drawer__contact-link">
              <span className="hdr-drawer__contact-icon"><Phone size={15} /></span>
              0374 888 419
            </a>
            <a href="mailto:contact@mklexpress.com" className="hdr-drawer__contact-link">
              <span className="hdr-drawer__contact-icon"><Mail size={15} /></span>
              contact@mklexpress.com
            </a>
          </div>
        </div>

        {/* Auth footer */}
        <div className="hdr-drawer__footer">
          {isLogged ? (
            <Link to={routesMap.THONG_KE_TIEN_HANG} className="hdr-drawer__auth-btn hdr-drawer__auth-btn--primary">
              <User size={18} />
              {customerPageLabel}
            </Link>
          ) : (
            <>
              <a href={SSO_URL} className="hdr-drawer__auth-btn hdr-drawer__auth-btn--primary">
                <LogIn size={18} />
                {t.login}
              </a>
              <a href={REGISTER_URL} className="hdr-drawer__auth-btn hdr-drawer__auth-btn--outline">
                <UserPlus size={18} />
                {t.register}
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
